import { useEffect, useState, useCallback } from "react";
import { updateModelConfig, queryAllDatasets } from "../../utils/fine-tuning-functions";

export default function ConfigureModelModal({ model, closeModal }) {
  const [modelConfig, setModelConfig] = useState({
    datasetLocation: "",
    hyperparameters: {
      max_seq_length: 2048,
      load_in_4bit: true,
      learning_rate: 0.0002,
      train_batch_size: 2,
      max_steps: 60,
      seed: 3407
    },
    baseModel: ""
  });
  
  const [datasets, setDatasets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  

  // Fetch available datasets
  const fetchDatasets = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await queryAllDatasets();

      const datasetNames = response.map(dataset => dataset.name);

      setDatasets(datasetNames);
    } catch (err) {
      console.error("Error fetching datasets:", err);
      setError("Failed to load available datasets. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDatasets();
  }, [fetchDatasets]);

  // Set initial model config when model data is available
  useEffect(() => {
    if (model) {
      setModelConfig({
        datasetLocation: model.dataset_location || "",
        hyperparameters: model.hyperparameters || {
          max_seq_length: 2048,
          load_in_4bit: true,
          learning_rate: 0.0002,
          train_batch_size: 2,
          max_steps: 60,
          seed: 3407
        },
        baseModel: model.base_model || ""
      });
    }
  }, [model]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setModelConfig((prevConfig) => ({
      ...prevConfig,
      [name]: value
    }));
  };

  const handleHyperparameterChange = (e) => {
    const { name, value, type } = e.target;
    
    setModelConfig((prevConfig) => ({
      ...prevConfig,
      hyperparameters: {
        ...prevConfig.hyperparameters,
        [name]: name === "load_in_4bit" 
          ? value === "true" 
          : type === "number" ? Number(value) : value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      await updateModelConfig(model.id, modelConfig);
      closeModal();
    } catch (error) {
      console.error("Error updating model configuration:", error);
      setError("Failed to update model configuration. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine if submit should be disabled
  const isFormInvalid = !modelConfig.baseModel || !modelConfig.datasetLocation;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-ktx-purple text-white px-6 py-4 rounded-t-lg flex justify-between items-center">
          <h2 className="text-xl font-semibold">Configure Model: {model?.name}</h2>
          <button 
            onClick={closeModal}
            className="text-white hover:text-gray-200 focus:outline-none"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Base Model Selection */}
            <div>
              <label htmlFor="baseModel" className="block text-sm font-medium text-gray-700 mb-1">
                Base Model
              </label>
              <input
                id="baseModel"
                name="baseModel"
                value={modelConfig.baseModel}
                onChange={handleInputChange}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple sm:text-sm"
              >

              </input>
              
              {modelConfig.baseModel === "custom" && (
                <input
                  type="text"
                  placeholder="Enter custom model identifier"
                  className="mt-2 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple sm:text-sm"
                  onChange={(e) => setModelConfig(prev => ({...prev, baseModel: e.target.value}))}
                />
              )}
              
              <p className="mt-1 text-xs text-gray-500">
                The pre-trained model you want to fine-tune
              </p>
            </div>
            
            {/* Dataset Selection Dropdown */}
            <div>
              <label htmlFor="datasetLocation" className="block text-sm font-medium text-gray-700 mb-1">
                Dataset
              </label>
              <div className="relative">
                {isLoading ? (
                  <div className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 text-gray-500 sm:text-sm">
                    Loading datasets...
                  </div>
                ) : (
                  <select
                    id="datasetLocation"
                    name="datasetLocation"
                    value={modelConfig.datasetLocation}
                    onChange={handleInputChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple sm:text-sm"
                  >
                    <option value="">Select a dataset</option>
                    {datasets.map((dataset, i) => (
                      <option key={i + 1} value={dataset}>
                        {dataset}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                The dataset you want to use for fine-tuning
              </p>
            </div>
            
            {/* Hyperparameters Section */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Hyperparameters</h3>
              
              <div className="bg-gray-50 rounded-md p-4 border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Max Sequence Length */}
                  <div>
                    <label htmlFor="max_seq_length" className="block text-sm font-medium text-gray-700 mb-1">
                      Max Sequence Length
                    </label>
                    <input
                      type="number"
                      id="max_seq_length"
                      name="max_seq_length"
                      value={modelConfig.hyperparameters.max_seq_length}
                      onChange={handleHyperparameterChange}
                      min="128"
                      max="4096"
                      step="128"
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Maximum length of input sequences (128-4096)
                    </p>
                  </div>
                  
                  {/* Learning Rate */}
                  <div>
                    <label htmlFor="learning_rate" className="block text-sm font-medium text-gray-700 mb-1">
                      Learning Rate
                    </label>
                    <input
                      type="number"
                      id="learning_rate"
                      name="learning_rate"
                      value={modelConfig.hyperparameters.learning_rate}
                      onChange={handleHyperparameterChange}
                      min="0.00001"
                      max="0.01"
                      step="0.00001"
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Learning rate for training (0.00001-0.01)
                    </p>
                  </div>
                  
                  {/* Train Batch Size */}
                  <div>
                    <label htmlFor="train_batch_size" className="block text-sm font-medium text-gray-700 mb-1">
                      Batch Size
                    </label>
                    <input
                      type="number"
                      id="train_batch_size"
                      name="train_batch_size"
                      value={modelConfig.hyperparameters.train_batch_size}
                      onChange={handleHyperparameterChange}
                      min="1"
                      max="64"
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Number of samples processed per batch
                    </p>
                  </div>
                  
                  {/* Max Steps */}
                  <div>
                    <label htmlFor="max_steps" className="block text-sm font-medium text-gray-700 mb-1">
                      Max Steps
                    </label>
                    <input
                      type="number"
                      id="max_steps"
                      name="max_steps"
                      value={modelConfig.hyperparameters.max_steps}
                      onChange={handleHyperparameterChange}
                      min="10"
                      max="1000"
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Maximum number of training steps
                    </p>
                  </div>
                  
                  {/* Load in 4bit */}
                  <div>
                    <label htmlFor="load_in_4bit" className="block text-sm font-medium text-gray-700 mb-1">
                      Load in 4-bit
                    </label>
                    <select
                      id="load_in_4bit"
                      name="load_in_4bit"
                      value={modelConfig.hyperparameters.load_in_4bit.toString()}
                      onChange={handleHyperparameterChange}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple sm:text-sm"
                    >
                      <option value="true">True</option>
                      <option value="false">False</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Use 4-bit quantization to reduce memory usage
                    </p>
                  </div>
                  
                  {/* Random Seed */}
                  <div>
                    <label htmlFor="seed" className="block text-sm font-medium text-gray-700 mb-1">
                      Random Seed
                    </label>
                    <input
                      type="number"
                      id="seed"
                      name="seed"
                      value={modelConfig.hyperparameters.seed}
                      onChange={handleHyperparameterChange}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Seed for reproducibility
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Error message */}
            {error && (
              <div className="rounded-md bg-red-50 p-4 border border-red-200">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Advanced configuration hint */}
            <div className="bg-blue-50 rounded-md p-4 border border-blue-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    These hyperparameters are optimized for most fine-tuning scenarios. Advanced users can adjust them as needed.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ktx-purple"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isFormInvalid || isSubmitting}
                className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ktx-purple ${
                  isFormInvalid || isSubmitting ? 'bg-ktx-purple-light cursor-not-allowed' : 'bg-ktx-purple hover:bg-ktx-purple-dark'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save Configuration'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}